import React from 'react';
import {
  Box,
  Container,
  Flex,
  Image,
  HStack,
  Button,
  Text,
  keyframes,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

// Subtle floating animation for the rook logo on landing pages that opt in.
const logoFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

function LandingHeader({
  onCtaClick,
  logoTo = '/landing',
  ctaLabel = 'Find How to Beat Competitors',
  signInLabel = 'Sign In',
  ctaFallbackPath = '/landing',
  // Opt-in agency-style polish. These default to off so other landing
  // pages (consumer, market-report) keep their existing chrome.
  showWordmark = false,
  glassmorphic = false,
  compact = false,
  floatLogo = false,
}) {
  const navigate = useNavigate();

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
    } else {
      navigate(ctaFallbackPath);
    }
  };

  // Container chrome differs between the default blue header and the
  // sticky glassmorphic header used on /agencies.
  const containerProps = glassmorphic
    ? {
        position: 'sticky',
        top: 0,
        bg: 'rgba(10, 24, 53, 0.7)',
        backdropFilter: 'saturate(140%) blur(12px)',
        borderBottom: '1px solid',
        borderColor: 'whiteAlpha.100',
        zIndex: 30,
      }
    : {
        position: 'relative',
        bg: 'linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)',
        zIndex: 20,
      };

  // Vertical padding is reduced ~20% in compact mode.
  const flexPy = compact ? 4 : 5;
  const logoH = compact
    ? { base: '34px', md: '56px' }
    : { base: '44px', md: '80px' };

  return (
    <Box {...containerProps}>
      <Container maxW="96vw" position="relative" zIndex={10}>
        <Flex justify="space-between" align="center" py={flexPy} px={{ base: 2, md: 4 }}>
          <Box
            as={RouterLink}
            to={logoTo}
            _hover={{ textDecoration: 'none' }}
            display="inline-flex"
            alignItems="center"
            gap={2}
          >
            <Image
              src="/output.png"
              alt="Logo"
              h={logoH}
              w="auto"
              objectFit="contain"
              filter="brightness(0) invert(1)"
              cursor="pointer"
              animation={floatLogo ? `${logoFloat} 4s ease-in-out infinite` : undefined}
            />
            {showWordmark && (
              <Text
                as="span"
                color="white"
                fontWeight="semibold"
                fontSize={{ base: 'lg', md: 'xl' }}
                letterSpacing="-0.01em"
                fontFamily="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                lineHeight={1}
                display={{ base: 'none', sm: 'inline-block' }}
              >
                Localytics
              </Text>
            )}
          </Box>
          <HStack spacing={{ base: 3, md: 6 }}>
            <Button
              as={RouterLink}
              to="/login"
              variant="ghost"
              color="white"
              fontWeight="medium"
              _hover={{ bg: "whiteAlpha.200", textDecoration: "none" }}
              size="md"
              fontSize={{ base: "sm", md: "md" }}
              display={{ base: "none", sm: "inline-flex" }}
            >
              {signInLabel}
            </Button>
            <Button
              onClick={handleCtaClick}
              bg="white"
              color="blue.600"
              _hover={{ bg: "whiteAlpha.900", transform: "translateY(-2px)", boxShadow: "lg", textDecoration: "none" }}
              size="md"
              fontWeight="semibold"
              px={6}
              fontSize={{ base: "sm", md: "md" }}
            >
              {ctaLabel}
            </Button>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}

export default LandingHeader;
